<template>
  <div>
    <AppLoadingPage v-if="$apollo.queries.event.loading" />
    <EventParticipantsTemplate
      v-else
      v-model:expanded="competitionsExpanded"
      :event="event"
      :discipline-name="disciplineName"
      :registrations="registrations"
    >
      <template #registrationLoading>
        <div
          v-if="registrationLoading && registrationsCount > 0"
          class="text-center py-4"
        >
          <h3>{{ loaded }} / {{ registrationsCount }}</h3>
          <v-progress-linear
            :model-value="registrationLazyloadingProgress"
            :buffer-value="registrationLazyloadingProgress"
            height="20"
            stream
          />
        </div>
      </template>
    </EventParticipantsTemplate>
  </div>
</template>

<script>
import { defineComponent, ref } from "vue";
import gql from "fraql";
import { useLazyLoadingQuery } from "@composables/lazy_loading_query";
import { apolloClient } from "@/plugins/apollo";

import AppLoadingPage from "@elements/AppLoadingPage.vue";
import EventParticipantsTemplate from "./EventParticipantsTemplate.vue";
import { useRouteScroll } from "@composables/route_scroll.ts";

const RegistrationFragment = gql`
  fragment _ on Registration {
    id
    status
    ${EventParticipantsTemplate.fragments.registrationFields}
    carriageNumber
    teamName

    vaultingLungerPerson {
      ${EventParticipantsTemplate.fragments.personFields}
    }

    athletes {
      ${EventParticipantsTemplate.fragments.personFields}
    }

    registrationPeople {
      ${EventParticipantsTemplate.fragments.registrationPerson}
    }

    registrantPerson {
      id
      name
    }

    horses {
      ${EventParticipantsTemplate.fragments.horseFields}
    }

    registrationHorses {
      ${EventParticipantsTemplate.fragments.registrationHorse}
    }

    entries(includeUnregistered: false) {
      id
      status
      resolution
      ${EventParticipantsTemplate.fragments.entryFields}
      paymentStatus
      teamName
      competition {
        id
        code
        ${EventParticipantsTemplate.fragments.competitionFields}
      }
      teamName
      confirmedAt
      registrationConfirmedAt 
    }
    
    createdAt
    allowedMutations
  }
`;

export default defineComponent({
  name: "EventParticipants",
  components: {
    EventParticipantsTemplate,
    AppLoadingPage
  },
  props: {
    eventId: {
      type: String,
      required: true
    },
    disciplineName: {
      type: String,
      required: true
    }
  },
  setup() {
    const scrollRoute = useRouteScroll();
    const registrationLazyLoading = useLazyLoadingQuery();

    const pendingOpenClassListId = ref(null);
    return {
      ...scrollRoute,

      pendingOpenClassListId,
      startRegistrationLazyLoading: registrationLazyLoading.startLazyLoading,
      loaded: registrationLazyLoading.currentTotalCount,
      registrationLazyLoadingState: registrationLazyLoading.loading,
      registrationLazyloadingProgress: registrationLazyLoading.loadingProgress
    };
  },
  data() {
    return {
      event: {
        id: null,
        competitions: []
      },
      competitionsExpanded: [],
      registrationsCount: 0,
      registrations: [],
      registrationLoading: true
    };
  },
  computed: {
    preferences() {
      let preferences = sessionStorage.getItem(
        `${this.eventId.split("-").join("")}`
      );
      if (preferences && (preferences = JSON.parse(preferences))) {
        return preferences.participants;
      } else return null;
    }
  },
  beforeMount() {
    this.flushEntries();
  },
  apollo: {
    event: {
      query: gql`
        query eventParticipantsTab($id: ID) {
          event(id: $id) {
            id
            startDate
            endDate
            status
            disciplineId
            discipline @client {
              id
              name
            }
            ${EventParticipantsTemplate.fragments.eventFields}
            competitions {
              id
              name
              description
              code
              startDate
              ${EventParticipantsTemplate.fragments.grandPrix}
              confirmedTeamResults {
                id
                teamName
              }
              ${EventParticipantsTemplate.fragments.restriction}
              ${EventParticipantsTemplate.fragments.competitionFields}              
              isTeamCompetition

              eventcodeId
              eventcode @client {
                id
                name
              }
              difficultyLevelId
              difficultyLevel @client {
                ${EventParticipantsTemplate.fragments.difficultyLevelFields}
              }
              competitionRuleId
              competitionRule @client {
                id
                name
              }
              competitionLevelId 
              competitionLevel @client {
                id
                name
              }
            }
          }
        }
      `,
      variables() {
        return {
          id: this.eventId
        };
      },
      result({ data, loading }) {
        if (loading || !data || !data.event) return;

        if (
          this.competitionsExpanded.length == 0 &&
          this.preferences?.competitionsExpanded &&
          data.event.competitions.length
        ) {
          this.competitionsExpanded =
            this.preferences.competitionsExpanded.filter(
              x => typeof x === "string"
            );
        }

        this.$nextTick(() => {
          this.openClassParticipantsList(this.pendingOpenClassListId);
        });
      },
      fetchPolicy: "cache-and-network"
    },
    registrations: {
      query: gql`
        query participantRegistrations($eventId: ID) {
          registrations(eventId: $eventId) {
            ${RegistrationFragment}
          }
        }
      `,
      variables() {
        return { eventId: this.eventId };
      },
      skip() {
        return this.registrationLoading;
      },
      update({ registrations }) {
        return registrations ?? [];
      },
      fetchPolicy: "cache-only"
    },
    registrationsCount: {
      query: gql`
        query participantRegistrationsCount($eventId: ID) {
          registrationsCount(eventId: $eventId)
        }
      `,
      variables() {
        return {
          eventId: this.eventId
        };
      },
      skip() {
        return this.$apollo.queries.event.loading;
      },
      async result({ data, loading }) {
        if (loading) return;
        if (!data || !data.registrationsCount) {
          this.registrationLoading = false;
          return;
        }
        this.registrationLoading = true;

        this.startRegistrationLazyLoading(
          gql` 
            query participantRegistrations($eventId: ID, $offset: Int, $limit: Int) {
              registrations(
                eventId: $eventId
                limit: $limit
                offset: $offset
              ) {
                ${RegistrationFragment}
              }
            }
          `,
          {
            totalCount: data.registrationsCount,
            perQueryLimit: 30,
            fieldName: "registrations",
            variables: {
              eventId: this.eventId
            }
          }
        ).then(() => {
          //if (registrations) this.registrations = registrations;
          this.registrationLoading = false;
          if (this.routeScrollParam) {
            this.scrollIntoElement();
          } else {
            this.$nextTick(() => {
              this.openClassParticipantsList(this.pendingOpenClassListId);
            });
          }
        });
      },
      fetchPolicy: "cache-and-network"
    }
  },
  methods: {
    flushEntries() {
      apolloClient.writeQuery({
        query: gql`
          query registrations($eventId: ID) {
            registrations (eventId: $eventId) {
              ${RegistrationFragment}
            }
          }
        `,
        data: {
          registrations: []
        },
        variables: {
          eventId: this.eventId
        }
      });
    },
    async openClassParticipantsList(classId) {
      if (!classId || !this.eventId || this.$apollo.queries.event.loading) {
        this.pendingOpenClassListId = classId; // if registrations or event is loading, put classId to variable
        return;
      }
      this.pendingOpenClassListId = null;
      this.singleList = false;

      await this.$nextTick();

      window.requestAnimationFrame(() => {
        const comp = this.event.competitions.find(x => x.id == classId);
        if (!comp) return;

        const classRow = this.$el.querySelector(
          `[data-competition-code="${comp.code}"]`
        );
        if (classRow) {
          const classRowBtn = classRow.querySelector(".participant-list");
          if (!classRowBtn.querySelector("i.fa-circle-caret-up")) {
            classRowBtn.click();
          }
          classRow.scrollIntoView({ block: "start" });
        }
      });
    }
  }
});
</script>
